// material ui icons
import CloseRounded from '@mui/icons-material/CloseRounded';

// material ui
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} [props.onClose]
 * @param {import('react').ReactNode} [props.title]
 * @param {number} [props.width]
 * @param {string} [props.anchor]
 * @param {import('react').ReactNode} [props.footer]
 * @param {import('react').ReactNode} [props.headerActions]
 * @param {import('react').ReactNode} [props.subheader]
 * @param {import('react').ReactNode} props.children
 */
export default function InsetDrawer({
  open,
  onClose,
  title,
  width = 800,
  anchor = 'right',
  footer = null,
  headerActions = null,
  subheader = null,
  children,
}) {
  const toggle = (next) => () => onClose(next);

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose ? toggle(false) : undefined}
      hideBackdrop={false}
      ModalProps={{ keepMounted: true }}
      sx={{ zIndex: (t) => t.zIndex.modal }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            top: 0,
            m: { xs: 1, sm: 2 },
            maxWidth: width,
            width:
              anchor === 'left' || anchor === 'right'
                ? {
                    xs: `calc(100% - ${theme.spacing(2)})`,
                    sm: `calc(100% - ${theme.spacing(2)})`,
                    xl: `calc(100% - ${theme.spacing(2)})`,
                  }
                : undefined,
            height:
              anchor === 'top' || anchor === 'bottom'
                ? {
                    xs: `calc(100% - ${theme.spacing(2)})`,
                    sm: `calc(100% - ${theme.spacing(4)})`,
                  }
                : `calc(100% - ${theme.spacing(4)})`,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 24,
            overflow: 'hidden',
          }),
        }
      }}
    >
      <DrawerContent
        onClose={onClose ? toggle(false) : undefined}
        title={title}
        footer={footer}
        headerActions={headerActions}
        subheader={subheader}
      >
        {children}
      </DrawerContent>
    </Drawer>
  );
}

function DrawerContent({
  onClose,
  title,
  footer,
  headerActions,
  subheader,
  children,
}) {
  return (
    <Box
      role="presentation"
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
      }}
    >
      {(title || onClose) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            justifyContent: 'space-between',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[50]
                : theme.palette.grey[900],
            p: 2,
            borderBottom: subheader ? 'none' : '1px solid',
            borderColor: 'divider',
          }}
        >
          {title && (
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {headerActions}
            {onClose && (
              <IconButton onClick={onClose} aria-label="Close">
                <CloseRounded />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      {subheader && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[50]
                : theme.palette.grey[900],
          }}
        >
          {subheader}
        </Box>
      )}
      <Box
        sx={{
          p: 2,
          flex: 1,
          height: '100%',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            height: '6px',
            width: '6px',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => theme.palette.divider,
            borderRadius: '3px',
          },
        }}
      >
        {children}
      </Box>
      {footer && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[50]
                : theme.palette.grey[900],
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}
